import type { IContentBase } from "@/types/content";
import type { QueryClient } from "@tanstack/vue-query";
import { isEqual } from "lodash-es";
import type { MaybeRefOrGetter } from "vue";

type ResponseDataMap = {
  contentId: IContentBase;
  taskId: { items: IContentBase[] };
};
type ReturnTypeMap = {
  contentId: IContentBase;
  taskId: IContentBase[];
};

type PayloadIdField = keyof ResponseDataMap;

export async function fetchSingleContentById<T extends PayloadIdField = PayloadIdField>(
  payload: Record<T, string | undefined>
): Promise<ReturnTypeMap[T] | null> {
  const { data } = await useGetRequest<ResponseDataMap[T]>("/content", {
    params: {
      contentId: "contentId" in payload ? payload.contentId : undefined,
      taskId: "taskId" in payload ? payload.taskId : undefined,
    },
  });
  if (!data || isEqual(data, { data: null })) return null;
  if ("items" in data) {
    data.items.forEach(item => {
      item.resultData = maybeParseJson(item.resultData, true);
    });
    return data.items as ReturnTypeMap[T];
  } else {
    data.resultData = maybeParseJson(data.resultData, true);
    return data as unknown as ReturnTypeMap[T]; // ts is dumb
  }
}

export function useSingleContentRequest<T extends PayloadIdField = PayloadIdField>(
  payload: Record<T, MaybeRefOrGetter<string>>,
  _enabled?: MaybeRefOrGetter<boolean>
) {
  const queryClient = useQueryClient();
  const contentId = computed(() =>
    "contentId" in payload ? (toValue(payload.contentId) as string) || "" : ""
  );
  const taskId = computed(() =>
    "taskId" in payload ? (toValue(payload.taskId) as string) || "" : ""
  );
  const enabled = computed(() => {
    return (
      (typeof _enabled === "undefined" || toValue(_enabled)) &&
      !!(contentId.value || taskId.value)
    );
  });

  const queryKey = computed(() => {
    return ["task-content", contentId.value ? contentId : taskId];
  });

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const data = await fetchSingleContentById<T>({
        // @ts-ignore - ts type inference for the generic T is broken here but works when the composable is used
        contentId: toValue(contentId.value) ? toValue(contentId.value) : undefined,
        taskId: toValue(taskId.value) ? toValue(taskId.value) : undefined,
      });

      if (Array.isArray(data)) {
        data.forEach(content => {
          queryClient.setQueryData(["task-content", content.id], content);
        });
      }

      return data;
    },
    staleTime: 2 * MINUTE,
    enabled,
  });

  return query;
}

export function updateSingleContentInQueryCache(
  contentId: MaybeRefOrGetter<string>,
  data: IContentBase,
  queryClient: QueryClient
) {
  queryClient.setQueryData(["task-content", toValue(contentId)], data);
}

export function updateSingleContentListQueryCache(
  taskId: MaybeRefOrGetter<string>,
  data: IContentBase,
  queryClient: QueryClient
) {
  const queryKey = ["task-content", toValue(taskId)];
  const existing = queryClient.getQueryData<IContentBase[]>(queryKey);
  if (!existing || !Array.isArray(existing))
    return queryClient.invalidateQueries({ queryKey });

  const updated = existing.map(item => {
    if (item.id === data.id) {
      return { ...item, ...data };
    }
    return item;
  });
  queryClient.setQueryData(queryKey, updated);
}
